import { inject, injectable } from 'tsyringe';
import { ResponseError } from '@bmai/telemetry-api-client';
import type {
    AdHocAggregateResult as AdHocAggregateResultDto,
    AdHocRecordsResult as AdHocRecordsResultDto,
    AdHocTrendResult as AdHocTrendResultDto,
    AnalyticsErrorReply,
} from '@bmai/telemetry-api-client';
import { telemetryClient } from '@/shared/TelemetryApiClient';
import { RuntimeConfigToken, type RuntimeConfig } from '@/runtime-config/RuntimeConfig';
import { resolveSignalTuning, type SignalTuning } from '@/runtime-config/SignalTuning';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { resolveDateRange, resolveWindowDays } from '@/features/global-filters/GlobalFilterTypes';
import { analyticsGlobalFields } from '@/features/global-filters/GlobalFilterScope';
import { mergeAnalyticsFilters } from './AnalyticsFilterOps';
import { humanUser } from '@/shared/analytics/MetricFormatting';
import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import {
    type AdHocAggregateQuery,
    type AdHocAggregateResult,
    type AdHocRecordsQuery,
    type AdHocRecordsResult,
    type AdHocTrendQuery,
    type AdHocTrendResult,
    type AnalyticsFilter,
    type AnalyticsMetric,
    type AnomalyEvent,
    type AnomalyQuery,
    type AnomalyResult,
    type AnomalyRow,
    type ConversationTurn,
    type DirectoryUser,
    type OlapTable,
    type RePromptOverheadQuery,
    type RePromptOverheadRow,
    type RePromptTurn,
    type RePromptTurnsQuery,
    type UserActivityFrequency,
    type UserActivityFrequencyQuery,
} from '@/shared/analytics/AnalyticsTypes';

/**
 * Live analytics gateway. Sends the declarative query model to telemetry's
 * analytics endpoints, which compile it to OLAP SQL server-side.
 *
 * This class used to compile DuckDB SQL in the browser and post the finished
 * string to the ad-hoc query endpoint. What remains here is the genuinely
 * client-side part: folding the page's active global filters into each query,
 * applying the runtime-config signal tuning, and turning the detectors' raw
 * anomaly measures into display copy.
 */
const ANALYTICS_GLOBAL_FIELDS = new Set<string>(analyticsGlobalFields());

@injectable()
export class OlapAnalyticsGateway implements IAnalyticsGateway {
    private readonly tuning: SignalTuning;

    constructor(
        @inject(RuntimeConfigToken) runtimeConfig: RuntimeConfig,
        @inject(GlobalFilterController) private readonly globalFilters: GlobalFilterController,
    ) {
        this.tuning = resolveSignalTuning(runtimeConfig);
    }

    /**
     * Folds the active global filters into a query: the global date range sets
     * explicit `[start, end)` bounds (overriding a rolling window), and the
     * global provider/person dimensions are merged into the query's filters.
     * Read at call time, so a filter change on the next query takes effect.
     */
    private withGlobals<T extends { filters?: AnalyticsFilter[]; start?: string; end?: string; ignoreGlobalFilters?: boolean }>(query: T): T {
        if (query.ignoreGlobalFilters === true) return query;
        const snapshot = this.globalFilters.snapshot();
        const { start, end } = resolveDateRange(snapshot.dateRange);
        return {
            ...query,
            start: query.start ?? start.toISOString(),
            end: query.end ?? end.toISOString(),
            filters: mergeAnalyticsFilters(query.filters ?? [], this.globalDims()).filters,
        };
    }

    /**
     * Folds the global filters into a window-based query (anomalies, activity,
     * re-prompt): the global provider/person dimensions are merged into filters,
     * and the global date range sets the rolling `windowDays` (its exact length;
     * a custom range resolves to its day span). Overrides any per-tile window so
     * these tiles honor the same date filter as the charts.
     */
    private withGlobalWindow<T extends { filters?: AnalyticsFilter[]; windowDays?: number }>(query: T): T {
        return {
            ...query,
            windowDays: resolveWindowDays(this.globalFilters.snapshot().dateRange),
            filters: mergeAnalyticsFilters(query.filters ?? [], this.globalDims()).filters,
        };
    }

    private globalDims(): AnalyticsFilter[] {
        return this.globalFilters.snapshot().dimensions.filter((filter) => ANALYTICS_GLOBAL_FIELDS.has(filter.field));
    }

    private withAnomalyTuning(query: AnomalyQuery): AnomalyQuery {
        return {
            ...query,
            windowDays: query.windowDays ?? this.tuning.anomalyWindowDays,
            limit: query.limit ?? this.tuning.anomalyLimit,
            decliningMinPriorCalls: query.decliningMinPriorCalls ?? this.tuning.decliningMinPriorCalls,
            decliningDropRatio: query.decliningDropRatio ?? this.tuning.decliningDropRatio,
            rePromptWindowMinutes: query.rePromptWindowMinutes ?? this.tuning.rePromptWindowMinutes,
        };
    }

    async aggregate(input: AdHocAggregateQuery): Promise<AdHocAggregateResult> {
        const query = this.withGlobals(input);
        return this.safe(async () => {
            const reply = await call(() =>
                telemetryClient.telemetryApi.postAnalyticsAggregate({
                    aggregateQueryBody: {
                        groupBy: query.groupBy,
                        metrics: query.metrics,
                        ...optional(query),
                        ...(query.orderBy === undefined ? {} : { orderBy: query.orderBy }),
                    },
                }),
            );
            return asTyped<AdHocAggregateResult>(reply);
        }, { rows: [], totals: ZERO_METRICS, distinctCount: 0 }, 'aggregate');
    }

    async trend(input: AdHocTrendQuery): Promise<AdHocTrendResult> {
        const query = this.withGlobals(input);
        return this.safe(async () => {
            const reply = await call(() =>
                telemetryClient.telemetryApi.postAnalyticsTrend({
                    trendQueryBody: {
                        bucket: query.bucket,
                        metrics: query.metrics,
                        ...optional(query),
                        ...(query.groupBy === undefined ? {} : { groupBy: query.groupBy }),
                    },
                }),
            );
            return asTyped<AdHocTrendResult>(reply);
        }, { points: [], totals: ZERO_METRICS }, 'trend');
    }

    async records(input: AdHocRecordsQuery): Promise<AdHocRecordsResult> {
        const query = this.withGlobals(input);
        return this.safe(async () => {
            const reply = await call(() =>
                telemetryClient.telemetryApi.postAnalyticsRecords({
                    recordsQueryBody: {
                        ...optional(query),
                        ...(query.dimensionKeys === undefined ? {} : { dimensionKeys: query.dimensionKeys }),
                        ...(query.hasSensitivityFlag === undefined ? {} : { hasSensitivityFlag: query.hasSensitivityFlag }),
                        ...(query.orderBy === undefined ? {} : { orderBy: query.orderBy }),
                    },
                }),
            );
            return asTyped<AdHocRecordsResult>(reply);
        }, { records: [], totalMatched: 0 }, 'records');
    }

    async anomalies(query: AnomalyQuery): Promise<AnomalyResult> {
        const q = this.withGlobalWindow(this.withAnomalyTuning(query));
        return this.safe(async () => {
            const reply = await call(() =>
                telemetryClient.telemetryApi.postAnalyticsAnomalies({
                    anomalyQueryBody: {
                        kind: q.kind,
                        ...(q.filters === undefined ? {} : { filters: q.filters }),
                        ...(q.windowDays === undefined ? {} : { windowDays: q.windowDays }),
                        ...(q.limit === undefined ? {} : { limit: q.limit }),
                        ...(q.decliningMinPriorCalls === undefined ? {} : { decliningMinPriorCalls: q.decliningMinPriorCalls }),
                        ...(q.decliningDropRatio === undefined ? {} : { decliningDropRatio: q.decliningDropRatio }),
                        ...(q.rePromptWindowMinutes === undefined ? {} : { rePromptWindowMinutes: q.rePromptWindowMinutes }),
                    },
                }),
            );
            const result = asTyped<{ rows: AnomalyRow[]; total: number }>(reply);
            const toEvent = anomalyEventMapper(q.kind, this.tuning);
            return { events: result.rows.map(toEvent), total: result.total };
        }, { events: [], total: 0 }, 'anomalies');
    }

    /**
     * Analytics is non-critical read data, so a failed query degrades to an empty
     * result — the surface renders its empty state instead of an error page. The
     * backend already returns empty for a no-data tenant; this also covers a
     * transport/runtime failure. The failure stays observable via a stable
     * `[analytics-unavailable]` console marker (mirrors GL-28's outage marker).
     */
    private async safe<T>(work: () => Promise<T>, empty: T, surface: string): Promise<T> {
        try {
            return await work();
        } catch (error) {
            console.warn(`[analytics-unavailable] ${surface} query failed — showing empty`, error);
            return empty;
        }
    }

    async userActivityFrequency(query: UserActivityFrequencyQuery): Promise<UserActivityFrequency[]> {
        const q = this.withGlobalWindow(query);
        const reply = await call(() =>
            telemetryClient.telemetryApi.postAnalyticsUserActivity({
                userActivityQueryBody: {
                    ...(q.filters === undefined ? {} : { filters: q.filters }),
                    ...(q.windowDays === undefined ? {} : { windowDays: q.windowDays }),
                },
            }),
        );
        return asTyped<{ rows: UserActivityFrequency[] }>(reply).rows;
    }

    async rePromptOverhead(query: RePromptOverheadQuery): Promise<RePromptOverheadRow[]> {
        const q = this.withGlobalWindow({
            ...query,
            rePromptWindowMinutes: query.rePromptWindowMinutes ?? this.tuning.rePromptWindowMinutes,
        });
        const reply = await call(() =>
            telemetryClient.telemetryApi.postAnalyticsRePromptOverhead({
                rePromptOverheadQueryBody: {
                    ...(q.filters === undefined ? {} : { filters: q.filters }),
                    ...(q.windowDays === undefined ? {} : { windowDays: q.windowDays }),
                    ...(q.rePromptWindowMinutes === undefined ? {} : { rePromptWindowMinutes: q.rePromptWindowMinutes }),
                },
            }),
        );
        return asTyped<{ rows: RePromptOverheadRow[] }>(reply).rows;
    }

    async rePromptTurns(query: RePromptTurnsQuery): Promise<RePromptTurn[]> {
        const q = this.withGlobalWindow({
            ...query,
            rePromptWindowMinutes: query.rePromptWindowMinutes ?? this.tuning.rePromptWindowMinutes,
        });
        const reply = await call(() =>
            telemetryClient.telemetryApi.postAnalyticsRePromptTurns({
                rePromptTurnsQueryBody: {
                    userId: q.userId,
                    ...(q.filters === undefined ? {} : { filters: q.filters }),
                    ...(q.windowDays === undefined ? {} : { windowDays: q.windowDays }),
                    ...(q.rePromptWindowMinutes === undefined ? {} : { rePromptWindowMinutes: q.rePromptWindowMinutes }),
                },
            }),
        );
        return asTyped<{ turns: RePromptTurn[] }>(reply).turns;
    }

    async directoryUserIds(): Promise<string[]> {
        const reply = await call(() => telemetryClient.telemetryApi.getAnalyticsDirectoryUserIds());
        return asTyped<{ userIds: string[] }>(reply).userIds;
    }

    async directoryUsers(): Promise<DirectoryUser[]> {
        const reply = await call(() => telemetryClient.telemetryApi.getAnalyticsDirectoryUsers());
        return asTyped<{ users: DirectoryUser[] }>(reply).users;
    }

    async getSessionConversation(sessionId: string): Promise<ConversationTurn[]> {
        const reply = await call(() =>
            telemetryClient.telemetryApi.getAnalyticsSessionsBySessionIdConversation({ sessionId }),
        );
        return asTyped<{ turns: ConversationTurn[] }>(reply).turns;
    }

    /**
     * Sends a raw OLAP SQL string to the telemetry endpoint — the tile-config
     * power-user fallback. `filters` are not compiled here: they travel as the
     * declarative model and telemetry composes the leading `scoped` CTE, so no
     * filter value is ever interpolated into SQL client-side.
     */
    async rawQuery(sql: string, filters?: AnalyticsFilter[]): Promise<OlapTable> {
        const reply = await call(() =>
            telemetryClient.telemetryApi.postOlapQuery({
                olapQueryBody: { sql, ...(filters === undefined || filters.length === 0 ? {} : { filters }) },
            }),
        );
        const table = asTyped<{ columns?: string[]; rows?: Record<string, unknown>[] }>(reply);
        if (!Array.isArray(table.rows) || !Array.isArray(table.columns)) {
            throw new Error('Telemetry OLAP query returned an unexpected response shape');
        }
        return { columns: table.columns, rows: table.rows };
    }
}

/** The time-window and filter fields every declarative body shares. */
function optional(query: { filters?: AnalyticsFilter[]; presentDimensions?: string[]; limit?: number; windowDays?: number; start?: string; end?: string }): Record<string, unknown> {
    return {
        ...(query.filters === undefined ? {} : { filters: query.filters }),
        ...(query.presentDimensions === undefined ? {} : { presentDimensions: query.presentDimensions }),
        ...(query.limit === undefined ? {} : { limit: query.limit }),
        ...(query.windowDays === undefined ? {} : { windowDays: query.windowDays }),
        ...(query.start === undefined ? {} : { start: query.start }),
        ...(query.end === undefined ? {} : { end: query.end }),
    };
}

type AnalyticsReply = AdHocAggregateResultDto | AdHocTrendResultDto | AdHocRecordsResultDto | AnalyticsErrorReply | object;

/** Unwraps the generated `200 | error` union, turning an error body into a throw. */
async function call<TReply>(request: () => Promise<TReply>): Promise<TReply> {
    try {
        return await request();
    } catch (cause) {
        if (cause instanceof ResponseError) throw new Error(await readResponseError(cause.response));
        throw cause;
    }
}

/**
 * Narrows a generated reply to its success shape.
 *
 * The generator collapses this contract's mapped types (`Record<AnalyticsMetric,
 * number>`, `Partial<Record<AnalyticsField, string>>`) to `object`, and models the
 * success/error reply as an untagged union — so this one cast per call is the
 * seam where the wire shape becomes the typed model. Error bodies are detected
 * structurally and raised before the cast, so a cast never applies to one.
 */
function asTyped<TResult>(reply: AnalyticsReply): TResult {
    const probe = reply as Partial<AnalyticsErrorReply>;
    if (typeof probe.error === 'string') {
        throw new Error(
            probe.detail !== undefined && probe.detail.length > 0 ? `${probe.error}: ${probe.detail}` : probe.error,
        );
    }
    return reply as TResult;
}

/** Empty metric totals for a degraded/empty analytics result. */
const ZERO_METRICS: Record<AnalyticsMetric, number> = {
    turns: 0,
    sessions: 0,
    users: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    avgOutputTokens: 0,
    avgConfidence: 0,
    avgModelFit: 0,
    allocatedCostUsd: 0,
};

function anomalyEventMapper(kind: AnomalyQuery['kind'], tuning: SignalTuning): (row: AnomalyRow) => AnomalyEvent {
    if (kind === 'session_drift') return (row) => toSessionDriftEvent(row, tuning);
    if (kind === 're_prompt') return (row) => toRePromptEvent(row, tuning);
    return (row) => toDecliningActivityEvent(row, tuning);
}

function subject(value: string | null): string {
    return value === null || value.length === 0 ? '—' : value;
}

function toSessionDriftEvent(row: AnomalyRow, tuning: SignalTuning): AnomalyEvent {
    const topics = Math.round(row.topics);
    const turns = Math.round(row.turns);
    const sessionId = subject(row.subjectId);
    return {
        label: `Session spans ${String(topics)} topics`,
        detail: `${humanUser(subject(row.userId))} · ${String(turns)} prompts`,
        severity: topics >= tuning.sessionDriftHighTopics ? 'high' : topics >= tuning.sessionDriftMediumTopics ? 'medium' : 'low',
        ...(sessionId === '—' ? {} : { drillKey: sessionId }),
        subject: humanUser(subject(row.userId)),
        count: topics,
    };
}

function toDecliningActivityEvent(row: AnomalyRow, tuning: SignalTuning): AnomalyEvent {
    const recent = Math.round(row.recent);
    const prior = Math.round(row.prior);
    const pct = prior > 0 ? Math.round((1 - recent / prior) * 100) : 100;
    return {
        label: `Activity down ${String(pct)}%`,
        detail: `${humanUser(subject(row.userId))} · ${String(recent)} vs ${String(prior)} prompts`,
        severity: recent === 0 ? 'high' : recent < tuning.decliningMediumRatio * prior ? 'medium' : 'low',
    };
}

function toRePromptEvent(row: AnomalyRow, tuning: SignalTuning): AnomalyEvent {
    const restarts = Math.round(row.restarts);
    const userId = subject(row.subjectId ?? row.userId);
    return {
        label: `${String(restarts)} near-identical re-prompt${restarts === 1 ? '' : 's'}`,
        detail: humanUser(userId),
        severity: restarts >= tuning.rePromptHighRestarts ? 'high' : restarts >= tuning.rePromptMediumRestarts ? 'medium' : 'low',
        ...(userId === '—' ? {} : { drillKey: userId }),
        subject: humanUser(userId),
        count: restarts,
        tokens: Math.round(row.rePromptTokens),
    };
}

async function readResponseError(response: Response): Promise<string> {
    const body = await response
        .clone()
        .json()
        .then((value) => value as { error?: string; detail?: string })
        .catch(() => null);
    if (body !== null && typeof body.error === 'string' && body.error.length > 0) {
        return body.detail !== undefined && body.detail.length > 0 ? `${body.error}: ${body.detail}` : body.error;
    }
    return `Telemetry analytics query failed (${String(response.status)} ${response.statusText})`;
}
